import { Component, HostBinding, Input, ViewChild } from '@angular/core';
import {
  AirlinePreference,
  AirlinePreferenceKeys,
  FlightPreference,
  FlightPreferenceKeys,
} from '@ag-common-lib/lib/models/utils/flight-preference.model';
import { FlightPreferenceFormService } from './flight-preference-form.service';
import {
  Airlines,
  AIRLINES_LOOKUP,
  FlightInfoKeys,
  SEAT_PREFERENCE_LOOKUP,
} from '@ag-common-lib/lib/models/registration/flight-information.model';
import { LookupKeys } from '@ag-common-lib/lib/models/crm/lookup.model';
import { BehaviorSubject } from 'rxjs';
import { RowValidatingEvent } from 'devextreme/ui/data_grid';
import { LayoutGridType } from '@ag-common-lib/lib/lists/layout/layout-grid-type.enum';
import { ModalWindowComponent } from 'ag-common-svc/lib/components/modal-window/modal-window.component';
import { AirlinePreferenceFormComponent } from './components/airline-preference-form/airline-preference-form.component';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';

@Component({
  selector: 'ag-shr-flight-preference-form',
  templateUrl: './flight-preference-form.component.html',
  styleUrl: './flight-preference-form.component.scss',
})
export class FlightPreferenceFormComponent {
  @HostBinding('class') className = 'flight-preference-form';
  @ViewChild('preferredAirlineModalRef', { static: true }) preferredAirlineModalComponent: ModalWindowComponent;
  @ViewChild('airlinePreferenceFormRef', { static: false })
  airlinePreferenceFormComponent: AirlinePreferenceFormComponent;

  @Input() set flightPreference(data: Partial<FlightPreference>) {
    this._formData = this.flightPreferenceService.getFormData(data);
    this.updateFilteredAirlines();
  }

  get flightPreference() {
    return this._formData;
  }

  @Input() isReadOnly: boolean = false;
  @Input() validationGroup: string;
  @Input() type: LayoutGridType = LayoutGridType.short;
  @Input() hasPermissionsToEdit: boolean = true;
  @Input() hasPermissionsToDelete: boolean = true;
  @Input() hasPermissionsToAdd: boolean = true;

  filteredAirlines$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  airlinePreferenceModalTitle = 'Add/Edit Preferred Airline';
  airlinePreferenceToEdit: Partial<AirlinePreference>;
  protected inEditMode$ = new BehaviorSubject<boolean>(false);
  inProgress$ = new BehaviorSubject<boolean>(false);

  _formData: FlightPreference = { airlinePreferences: [] };

  constructor(private flightPreferenceService: FlightPreferenceFormService) {}

  protected readonly AIRLINES_LOOKUP = AIRLINES_LOOKUP;
  protected readonly LookupKeys = LookupKeys;
  protected readonly FlightInfoKeys = FlightInfoKeys;
  protected readonly SEAT_PREFERENCE_LOOKUP = SEAT_PREFERENCE_LOOKUP;
  protected readonly Airlines = Airlines;
  protected readonly LayoutGridType = LayoutGridType;

  updateFilteredAirlines(airlineToSkip?: Airlines) {
    const selectedAirlines = this.flightPreference.airlinePreferences
      .map(pref => pref.airline)
      .filter(airline => (airlineToSkip ? airline !== airlineToSkip : true));

    const filteredAirlines = [...AIRLINES_LOOKUP].filter(airline => !selectedAirlines.includes(airline.value));
    this.filteredAirlines$.next(filteredAirlines);
  }

  onRowSaved() {
    this.updateFilteredAirlines();
  }

  protected onRowValidating = (e: RowValidatingEvent<AirlinePreference>): void => {
    const data = Object.assign({}, e?.oldData, e?.newData);
    const isValid = !(data.isFirstChoiceAirline && data.isSecondChoiceAirline);

    e.isValid = isValid;

    const dataGrid = e.component;
    const rowIndex = dataGrid.getRowIndexByKey(e.key);

    if (!isValid) {
      dataGrid
        .getCellElement(rowIndex, AirlinePreferenceKeys.isFirstChoiceAirline)
        .classList.add('dx-validator', 'dx-datagrid-invalid');
      dataGrid
        .getCellElement(rowIndex, AirlinePreferenceKeys.isSecondChoiceAirline)
        .classList.add('dx-validator', 'dx-datagrid-invalid');
    }

    if (!data.airline) {
      dataGrid
        .getCellElement(rowIndex, AirlinePreferenceKeys.airline)
        .classList.add('dx-validator', 'dx-datagrid-invalid');
    }
  };

  showAddPreferredAirlinePopup = (): void => {
    this.airlinePreferenceModalTitle = 'Add Preferred Airline';
    this.airlinePreferenceToEdit = {};
    this.updateFilteredAirlines();
    this.preferredAirlineModalComponent.showModal();
  };

  showEditPopup = ({ row: { data } }): void => {
    this.airlinePreferenceModalTitle = 'Edit Preferred Airline';
    this.airlinePreferenceToEdit = Object.assign(
      {},
      {
        [AirlinePreferenceKeys.airline]: data?.[AirlinePreferenceKeys.airline] ?? null,
        [AirlinePreferenceKeys.otherPreferredAirline]: data?.[AirlinePreferenceKeys.otherPreferredAirline] ?? null,
        [AirlinePreferenceKeys.loyaltyNumber]: data?.[AirlinePreferenceKeys.loyaltyNumber] ?? null,
        [AirlinePreferenceKeys.isFirstChoiceAirline]: data?.[AirlinePreferenceKeys.isFirstChoiceAirline] ?? false,
        [AirlinePreferenceKeys.isSecondChoiceAirline]: data?.[AirlinePreferenceKeys.isSecondChoiceAirline] ?? false,
      },
    );
    this.updateFilteredAirlines(data.airline);
    this.preferredAirlineModalComponent.showModal();
  };

  protected enableEditMode = () => {
    this.inEditMode$.next(true);
  };

  protected disableEditMode = () => {
    this.inEditMode$.next(false);
  };

  protected handlePreferredAirlineFormPopupClose = () => this.airlinePreferenceFormComponent.onCancelEdit();

  protected handlePreferredAirlineSave = async e => {
    this.inProgress$.next(true);

    const isValid = await validateDxGroups([this.validationGroup]);
    if (!isValid) {
      this.inProgress$.next(false);
      return;
    }

    this.flightPreference.airlinePreferences = this._normalizePreferredAirlines();
    this.preferredAirlineModalComponent.forceCloseModal();
    this.inProgress$.next(false);
  };

  private _normalizePreferredAirlines = (): AirlinePreference[] => {
    if (!this.airlinePreferenceToEdit) {
      return this.flightPreference?.[FlightPreferenceKeys.airlinePreferences];
    }
    let isUpdated = false;
    const normalizedAirlinePreferences = this.flightPreference?.[FlightPreferenceKeys.airlinePreferences].map(
      airlinePreferences => {
        if (this.airlinePreferenceToEdit?.airline === airlinePreferences?.airline) {
          isUpdated = true;
          return this.airlinePreferenceToEdit;
        }
        const normalizedAirlinePreference = Object.assign({}, airlinePreferences);

        return normalizedAirlinePreference;
      },
    );

    if (!isUpdated) {
      normalizedAirlinePreferences.push(Object.assign({}, this.airlinePreferenceToEdit));
    }

    return normalizedAirlinePreferences as AirlinePreference[];
  };
}
